import { ChevronLeftIcon } from '@heroicons/react/16/solid';
import { PlusIcon } from '@heroicons/react/20/solid';
import { DocumentIcon } from '@heroicons/react/24/outline';
import { PencilIcon } from '@heroicons/react/24/solid';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams
} from 'react-router-dom';

import { AnimatePresence, motion } from 'framer-motion';
import useBulkOrderings from 'src/features/product/product-bulk-orderings/hooks/useBulkOrderings';
import ProductDetailOrdersTab from 'src/features/product/product-bulk-orderings/ui/ProductDetailOrdersTab';
import { useProductById } from 'src/features/product/product-detail/hooks/useProductById';
import useProductQuotes from 'src/features/product/product-detail/hooks/useProductQuotes';
import useProductUpdateInputModal from 'src/features/product/product-detail/hooks/useProductUpdateInputModal';
import ProductDetailFilesTab from 'src/features/product/product-detail/ui/files-tab/ProductDetailFilesTab';
import ProductRfqDrawer from 'src/features/product/product-detail/ui/product-rfq-drawer/ProductRfqDrawer';
import ProductDetailFormModal from 'src/features/product/product-detail/ui/ProductDetailFormModal';
import AddressSelectionModal from 'src/features/product/product-detail/ui/quotes-tab/AddressSelectionModal';
import ProductDetailQuotesTab from 'src/features/product/product-detail/ui/quotes-tab/ProductDetailQuotesTab';
import ProductUpdateInputModal from 'src/features/product/product-detail/ui/update-modal/ProductUpdateInputModal';
import UpdateProductImage from 'src/features/product/product-detail/ui/UpdateProductImage';
import ProductDetailUpdatesTab from 'src/features/product/product-detail/ui/updates-tab/ProductDetailUpdatesTab';
import getStageFromLifecycle from 'src/features/product/product-list/utils/getStageFromLifecycle';
import ProductDetailSamplingsTab from 'src/features/product/product-sampling/ui/product-detail-samplings-tab/ProductDetailSamplingsTab';
import { FF_CAVELA_DIRECT } from 'src/settings';
import { StageTag } from 'src/shared/components';
import { ROUTE_PATHS } from 'src/shared/config/routes';
import {
  CTA_BULK_ORDERING,
  CTA_REORDER,
  CtaTemplateId
} from 'src/shared/constants/update';
import { useFeatureFlag } from 'src/shared/hooks';
import { usePageTitle } from 'src/shared/hooks';
import {
  ProductById,
  ProductLifecycleStage,
  Stage
} from 'src/shared/types/product';

import { Spinner, Tab, useDisclosure } from 'src/shared/components/ui';
import { ButtonV2, TabsV2, Typography } from 'src/shared/components/ui';

import NotFoundPage from '../not-found-page/NotFoundPage';

interface OrderCta {
  label: string;
  data: { name: string; quantity?: number | null };
  ctaType: CtaTemplateId;
}

const ProductDetailPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab');
  const updateId = searchParams.get('update') ?? undefined;
  const animate = searchParams.get('animate');
  const { data, isLoading, isSuccess, error, isError } = useProductById(
    id as string
  );

  const { data: bulkOrderings, isLoading: isBulkOrderingsLoading } =
    useBulkOrderings(id ?? '');
  const { setData: setUpdateInputData } = useProductUpdateInputModal();
  const {
    data: quotes,
    isSuccess: isQuotesSuccess,
    isLoading: isQuotesLoading
  } = useProductQuotes(id ?? '');

  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const {
    isOpen: isOpenProductRfqDrawer,
    onOpen: onOpenProductRfqDrawer,
    onOpenChange: onOpenChangeProductRfqDrawer
  } = useDisclosure();

  const { isFeatureEnabled: showCavelaDirect } =
    useFeatureFlag(FF_CAVELA_DIRECT);

  const isCavelaDirect = useMemo(
    () => (data?.tags ?? []).includes('cavela_direct'),
    [data?.tags]
  );

  const hasSamples = useMemo(() => data?.has_samples, [data?.has_samples]);

  useEffect(() => {
    if (!animate) {
      return;
    }
    if (!isSuccess || !data) {
      return;
    }
    if ((data.tags ?? []).includes('cavela_direct')) {
      return;
    }
    const timeoutId = window.setTimeout(() => {
      const next = new URLSearchParams(searchParams);

      next.delete('animate');
      setSearchParams(next);
    }, 2500);

    return () => window.clearTimeout(timeoutId);
  }, [animate, isSuccess, data, searchParams, setSearchParams]);

  // stripe success
  useEffect(() => {
    if (searchParams.get('refreshStripeData') !== 'true' || !id) {
      return;
    }

    queryClient.invalidateQueries({ queryKey: ['product-quotes', id] });
    queryClient.invalidateQueries({ queryKey: ['bulk-orderings', id] });
    queryClient.invalidateQueries({ queryKey: ['product', id] });

    const next = new URLSearchParams(searchParams);

    next.delete('refreshStripeData');
    setSearchParams(next, { replace: true });
  }, [id, searchParams, setSearchParams, queryClient]);

  // The order the header CTA starts: more of the last bulk order, or a first
  // one from a quote. Cavela Direct products order from their quote summary.
  const orderCta = useMemo((): OrderCta | null => {
    if (isCavelaDirect) {
      return null;
    }

    const name = data?.detail?.name ?? '';

    if (bulkOrderings?.length) {
      return {
        label: 'Order more',
        ctaType: CTA_REORDER,
        data: { name, quantity: bulkOrderings[0]?.order_quantity ?? 1 }
      };
    }

    if (quotes?.length) {
      return {
        label: 'Order now',
        ctaType: CTA_BULK_ORDERING,
        data: {
          name,
          quantity: quotes[0]?.product_quotes?.[0]?.quantity ?? 100
        }
      };
    }

    return null;
  }, [isCavelaDirect, data?.detail?.name, bulkOrderings, quotes]);

  // The address modal that starts an order is open while ?newOrder=true is in
  // the URL, so the products list can link straight to it.
  const newOrderSearch = useMemo(() => {
    const next = new URLSearchParams(searchParams);

    next.set('newOrder', 'true');

    return `?${next.toString()}`;
  }, [searchParams]);
  const isNewOrderOpen =
    searchParams.get('newOrder') === 'true' && orderCta !== null;

  const closeNewOrder = () => {
    const next = new URLSearchParams(searchParams);

    next.delete('newOrder');
    setSearchParams(next, { replace: true });
  };

  const activeTab = useMemo(() => {
    if (updateId) {
      return 'updates';
    }
    if (
      tab === 'samples' &&
      isCavelaDirect &&
      showCavelaDirect &&
      !hasSamples
    ) {
      return 'updates';
    }

    return tab || 'updates';
  }, [tab, updateId, isCavelaDirect, showCavelaDirect, hasSamples]);

  useEffect(() => {
    if (!id) return;
    queryClient.invalidateQueries({ queryKey: ['product', id] });
    queryClient.invalidateQueries({ queryKey: ['bulk-orderings', id] });
    queryClient.invalidateQueries({ queryKey: ['product-quotes', id] });
  }, [activeTab, id, queryClient]);

  const allFiles = useMemo(() => {
    const cleanedUplaodedFiles = data?.all_files?.uploaded_files?.map(
      (file) => ({
        ...file,
        id: file?.file?.id
      })
    );

    const cleanedBulkOrderingFiles = data?.all_files?.bulk_ordering_files?.map(
      (file) => ({
        ...file,
        id: file?.file?.id
      })
    );

    return [
      ...(data?.all_files?.product_files || []),
      ...(cleanedUplaodedFiles || []),
      ...(cleanedBulkOrderingFiles || [])
    ];
  }, [data]);

  const rfQId = useMemo(() => {
    return data && data.rfq && data?.rfq.id;
  }, [data]);

  const headerStage = useMemo((): Stage => {
    if (data?.approval_status === 'rejected') {
      return 'rejected';
    }

    return (
      getStageFromLifecycle(
        data?.detail?.lifecycle as ProductLifecycleStage[]
      ) ?? 'draft'
    );
  }, [data?.approval_status, data?.detail?.lifecycle]);

  usePageTitle(isSuccess ? `${data?.detail?.name} | Cavela` : undefined);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'updates':
        return (
          <ProductDetailUpdatesTab
            product={data as ProductById}
            updateId={updateId}
          />
        );
      case 'timeline':
        return 'Timeline';
      case 'quotes':
        return (
          <ProductDetailQuotesTab
            product={data as ProductById}
            data={quotes ?? []}
            isSuccess={isQuotesSuccess}
            bulkOrderings={bulkOrderings ?? []}
            showCavelaDirectQuoteSummary={isCavelaDirect && showCavelaDirect}
            onOpenRfqDrawer={onOpenProductRfqDrawer}
          />
        );
      case 'samples':
        return <ProductDetailSamplingsTab product={data as ProductById} />;
      case 'orders':
        return (
          <ProductDetailOrdersTab
            bulkOrderings={bulkOrderings ?? []}
            isLoading={isBulkOrderingsLoading}
          />
        );
      case 'files':
        return <ProductDetailFilesTab files={allFiles} />;

      default:
        return (
          <ProductDetailUpdatesTab
            product={data as ProductById}
            updateId={updateId}
          />
        );
    }
  };

  const renderCta = () => {
    if (!orderCta) {
      return null;
    }

    return (
      <ButtonV2
        as={Link}
        to={newOrderSearch}
        href={newOrderSearch}
        size="md"
        color="primary"
        startContent={<PlusIcon className="size-4 pointer-events-none" />}
      >
        {orderCta.label}
      </ButtonV2>
    );
  };

  if (isLoading && animate) {
    return (
      <div
        className="flex justify-center items-center h-full bg-primary fixed inset-0 z-50"
        style={{
          backgroundImage: `
          linear-gradient(to right, rgba(255, 255, 255, 0.15) 1px, transparent 1px),
          linear-gradient(to bottom, rgba(255, 255, 255, 0.15) 1px, transparent 1px)
        `,
          backgroundSize: '40px 40px',
          backgroundColor: '#000EEF'
        }}
      />
    );
  }

  if (isLoading || isQuotesLoading || isBulkOrderingsLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error?.message === 'Not found') {
    return (
      <NotFoundPage
        className="h-fit relative top-[30%]"
        title="Product not found"
        description="The product you are looking for does not exist."
      />
    );
  }

  if (isError) {
    return null;
  }

  return (
    <div
      data-testid="products-display"
      className="h-full py-4 flex flex-col relative"
    >
      <div
        aria-label="test"
        className="flex items-center justify-between gap-6 mb-4 px-6 mobile:flex-col mobile:items-start mobile:gap-2"
      >
        {/* Row 1 — desktop: back + image + name + stage; mobile: back + name only */}
        <div className="flex items-center flex-1 mobile:w-full">
          <ButtonV2
            onPress={() => navigate(ROUTE_PATHS.PRODUCTS)}
            isIconOnly
            color="fourth"
          >
            <ChevronLeftIcon className="size-6" />
          </ButtonV2>
          <div className="flex items-center flex-1 gap-3">
            <div className="mobile:hidden">
              <UpdateProductImage product={data as ProductById} />
            </div>
            <div className="group flex items-center gap-3">
              <Typography
                className="whitespace-nowrap mobile:whitespace-normal mobile:text-2xl"
                as="h1"
                variant="h2"
                weight="bold"
              >
                {data?.detail?.name}
              </Typography>
              <ButtonV2
                isIconOnly
                variant="flat"
                color="default"
                className="group-hover:opacity-100 opacity-0 group-hover:flex hidden"
                onPress={onOpen}
                startContent={
                  <PencilIcon className="size-4 pointer-events-none" />
                }
              />
            </div>
            <div className="mobile:hidden">
              <StageTag type={headerStage} />
            </div>
          </div>
        </div>

        {/* Row 2 — desktop: action buttons; mobile: image + stage tag + action buttons */}
        <div className="flex items-center gap-2 mobile:w-full mobile:justify-between">
          <div className="hidden mobile:flex items-center gap-3">
            <UpdateProductImage product={data as ProductById} />
            <StageTag type={headerStage} />
          </div>
          <div className="flex items-center gap-2">
            {rfQId && (
              <ButtonV2
                onPress={() => {
                  onOpenProductRfqDrawer();
                }}
                size="md"
                color="tertiary"
                startContent={
                  <DocumentIcon className="size-4 pointer-events-none" />
                }
              >
                Product specs
              </ButtonV2>
            )}
            {renderCta()}
          </div>
        </div>
      </div>

      <div className="px-3">
        <TabsV2
          selectedKey={activeTab}
          onSelectionChange={(key) => {
            navigate(`${ROUTE_PATHS.PRODUCTS}/${id}?tab=${key}`);
          }}
        >
          <Tab key="updates" title="Updates" />
          <Tab key="quotes" title={isCavelaDirect ? 'Quote' : 'Quotes'} />
          {!(isCavelaDirect && showCavelaDirect && !hasSamples) && (
            <Tab key="samples" title="Samples" />
          )}
          <Tab key="orders" title="Orders" />
          <Tab key="files" title="Files" />
        </TabsV2>
      </div>
      <div className="w-full h-[1px] bg-gray-4 relative top-[-4px]" />
      <div className="py-4 px-6 flex-grow">{renderTabContent()}</div>

      <ProductDetailFormModal
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        product={data as ProductById}
      />

      <AnimatePresence>
        {animate && (
          <motion.div
            className="fixed inset-0 z-50"
            initial={{ clipPath: 'inset(0% 0 0% 0)' }}
            animate={{ clipPath: 'inset(0% 0 100% 0)' }}
            exit={{ clipPath: 'inset(0% 0 100% 0)' }}
            transition={{ duration: 1.2, ease: 'easeInOut', delay: 0.5 }}
            style={{
              backgroundImage: `
                linear-gradient(to right, rgba(255, 255, 255, 0.15) 1px, transparent 1px),
                linear-gradient(to bottom, rgba(255, 255, 255, 0.15) 1px, transparent 1px)
              `,
              backgroundSize: '40px 40px',
              backgroundColor: '#000EEF'
            }}
          />
        )}
      </AnimatePresence>

      <AddressSelectionModal
        isOpen={isNewOrderOpen}
        onOpenChange={(open) => {
          if (!open) {
            closeNewOrder();
          }
        }}
        onConfirm={(address) => {
          closeNewOrder();
          if (orderCta) {
            setUpdateInputData({ ...orderCta.data, address }, orderCta.ctaType);
          }
        }}
        title="Select shipping address"
        confirmLabel="Continue"
      />
      <ProductUpdateInputModal />
      <ProductRfqDrawer
        rfq={data?.rfq}
        isOpen={isOpenProductRfqDrawer}
        onOpenChange={onOpenChangeProductRfqDrawer}
      />
    </div>
  );
};

export default ProductDetailPage;
